<script setup lang="ts">
  import { useGetFeatureFlags } from "@/api/config/config"
  import { useGetLoyaltyStatus } from "@/api/loyalty/loyalty"
  import { isTopPrizeType } from "@/constants/loyalty"
  import { MyAccountRoute } from "@/constants/routes/myAccount"
  import { useGetAccountId } from "@/lib/useGetAccountId"
  import ErrorReporter from "@/services/ErrorReporter"
  import TrackingService from "@/services/TrackingService"
  import { computed, unref, watch } from "vue"
  import { useRoute, useRouter } from "vue-router"
  import { Button } from "@/components/ui/button"
  import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogIllustration,
    DialogTitle,
  } from "@/components/ui/dialog"

  import { useYouWonModal } from "@/views/myaccount/loyalty/composables/useYouWonModal"
  import { useYouWonSuppression } from "@/views/myaccount/loyalty/composables/useYouWonSuppression"

  import trophy from "@/assets/illustrations/trophy.png"

  // Mounted app-wide (App.web.vue); `entries: false` skips the entry calc the modal doesn't need.
  const router = useRouter()
  const route = useRoute()
  const { accountId } = useGetAccountId()
  const { data: flags } = useGetFeatureFlags()
  const { isSuppressed, dismiss } = useYouWonSuppression()

  // Gate on the soRewardingV2 kill-switch and a small set of non-flow pages; also
  // skip the loyalty-status call entirely while suppressed (dismissed < 5 weeks ago).
  const allowedPaths: string[] = [
    MyAccountRoute.home,
    MyAccountRoute.profile,
    MyAccountRoute.readings,
    MyAccountRoute.rewards,
  ]
  const canShow = computed(
    () => !!flags.value?.soRewardingV2 && allowedPaths.includes(route.path),
  )

  const { data: loyaltyStatus } = useGetLoyaltyStatus(
    accountId,
    { entries: false },
    {
      query: {
        enabled: computed(
          () => !!accountId.value && canShow.value && !isSuppressed.value,
        ),
        throwOnError: (error) => {
          ErrorReporter.report(error, { accountId: unref(accountId) })
          return false
        },
      },
    },
  )

  const draws = computed(() => loyaltyStatus.value?.draws ?? null)
  const { showModal, wonPrize, wonDrawName } = useYouWonModal(
    draws,
    isSuppressed,
  )

  // Actual visibility also respects the flag + allowed-page gate.
  const open = computed(() => showModal.value && canShow.value)

  const isTopPrize = computed(
    () => !!wonPrize.value && isTopPrizeType(wonPrize.value.type),
  )

  // Reward name comes straight from the BE (wonPrize.name). The winners list
  // still maps from the prize type because its endpoint omits the name.
  const prizeName = computed(() => wonPrize.value?.name ?? null)

  function dismissWith(method: string) {
    TrackingService.sendEvent("so_rewarding_you_won_dismissed", {
      eventCategory: "so_rewarding",
      eventAction: "you_won_dismissed",
      entityType: "modal",
      entityVariant: method,
    })
    dismiss()
  }

  function goToAccountHome() {
    dismissWith("account_home")
    router.push(MyAccountRoute.home)
  }

  function goToSoRewarding() {
    dismissWith("so_rewarding")
    router.push(MyAccountRoute.rewards)
  }

  // radix fires update:open(false) for every dialog-driven close path (X, ESC,
  // overlay); treat them all as a dismiss.
  function handleOpenChange(open: boolean) {
    if (!open) dismissWith("overlay_or_key")
  }

  // Impression: fire once the modal becomes visible.
  watch(open, (visible) => {
    if (!visible) return
    TrackingService.sendEvent("so_rewarding_you_won_shown", {
      eventCategory: "so_rewarding",
      eventAction: "you_won_shown",
      entityType: "modal",
      entityVariant: wonPrize.value?.type,
    })
  })
</script>

<template>
  <Dialog :open="open" @update:open="handleOpenChange">
    <DialogContent data-testid="you-won-modal-content">
      <template #illustration>
        <DialogIllustration :src="trophy" alt="Prize trophy" />
      </template>
      <DialogHeader>
        <DialogTitle
          data-testid="you-won-modal-title"
          class="text-secondary-700"
        >
          <template v-if="isTopPrize">
            Congratulations!<br />You're our top prize winner!
          </template>
          <template v-else>Congratulations, you won!</template>
        </DialogTitle>
      </DialogHeader>

      <div class="space-y-6">
        <p data-testid="you-won-modal-subline">
          You're a winner in our {{ wonDrawName ?? "latest prize draw" }}!
        </p>

        <div
          v-if="wonPrize"
          class="space-y-1 rounded bg-secondary-200 px-4 py-4"
          data-testid="you-won-modal-prize"
        >
          <p class="h4" data-testid="you-won-modal-prize-name">
            {{ prizeName }}
          </p>
          <p data-testid="you-won-modal-fulfilment">
            Depending on your prize, we'll either add it to your energy account
            or be in touch about how to redeem it.
          </p>
        </div>
      </div>

      <DialogFooter class="flex-col-reverse items-center sm:flex-row">
        <Button
          variant="outline"
          data-testid="you-won-modal-account-home"
          @click="goToAccountHome"
        >
          Go to Account Home
        </Button>
        <Button
          variant="primary"
          data-testid="you-won-modal-so-rewarding"
          @click="goToSoRewarding"
        >
          Go to So Rewarding
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
